// Shared playback state machine for voice messages. Consumes a
// `VoicePlaybackDriver` injected through `<VoicePlaybackProvider>`
// and exposes `{ isPlaying, progressMs, togglePlay }` to the bubble.
// All audio I/O lives in the driver; this hook owns idle → playing
// → ended transitions, progress throttling, and waveform caching.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Message } from '../../sync/persistence';
import {
  deterministicWaveform,
  VOICE_WAVEFORM_BUCKETS,
  type VoicePlaybackHandle,
  type VoiceSource,
} from './voicePlaybackDriver';
import { useVoicePlaybackDriver } from './VoicePlaybackProvider';

export interface UseVoicePlaybackOptions {
  readonly message: Message;
  /**
   * Builds a `VoiceSource` from the persisted message body. Each host
   * supplies this: web decodes the base64 envelope to a Blob URL,
   * desktop returns the base64+mime directly. Returns `null` when the
   * body isn't a voice envelope so the hook stays a no-op for text
   * rows.
   */
  readonly resolveSource: (message: Message) => VoiceSource | null;
  /**
   * Invoked when the driver fails to start playback (codec not
   * decodable, autoplay blocked, …). The hook still resets to idle;
   * this lets the host surface a visible error (e.g. a toast) instead
   * of the previous silent failure.
   */
  readonly onError?: ((err: unknown) => void) | undefined;
}

export interface UseVoicePlaybackResult {
  readonly isPlaying: boolean;
  readonly progressMs: number;
  /**
   * Real clip duration (ms) learned from the audio element once it
   * loads metadata, or 0 before then. The bubble falls back to this
   * when the envelope's recorded duration is missing/0 so the waveform
   * progress fill still advances.
   */
  readonly durationMs: number;
  readonly togglePlay: () => void;
  /** Set playback speed (1 = normal). Applies live and to the next play. */
  readonly setRate: (rate: number) => void;
  /** 32-bucket amplitude waveform, normalised 0..1. Updates async as
   *  the driver decodes the audio. Starts as a deterministic fallback
   *  keyed off the message id so the bubble renders bars on first
   *  paint without waiting for `extractWaveform`. */
  readonly waveform: ReadonlyArray<number>;
}

/**
 * Drives a `<VoiceMessageBubble>`'s playback state for one message.
 * No-op when no `<VoicePlaybackProvider>` is mounted upstream — the
 * bubble's `onTogglePlay` callback then resolves to the caller's
 * original no-op, matching the legacy behaviour.
 */
export function useVoicePlayback(
  opts: UseVoicePlaybackOptions,
): UseVoicePlaybackResult {
  const { message, resolveSource, onError } = opts;
  const driver = useVoicePlaybackDriver();

  const [isPlaying, setIsPlaying] = useState(false);
  const [progressMs, setProgressMs] = useState(0);
  const [durationMs, setDurationMs] = useState(0);
  const [waveform, setWaveform] = useState<ReadonlyArray<number>>(() =>
    deterministicWaveform(message.id, VOICE_WAVEFORM_BUCKETS),
  );
  const handleRef = useRef<VoicePlaybackHandle | null>(null);
  // True from the moment `togglePlay` kicks off `driver.play(...)` until
  // the handle resolves (or fails). Lets a second tap DURING startup
  // cancel cleanly — without it, the second tap saw `handleRef === null`
  // and started a *second* playback instead of pausing, so the bubble
  // looked stuck "playing" and never paused.
  const startingRef = useRef(false);
  const mountedRef = useRef(true);
  // Current playback speed — applied to the live handle and seeded into
  // the next `play()` so speed persists across pause/resume.
  const rateRef = useRef(1);

  // Resolve the playback source up-front so togglePlay doesn't have to
  // pay the parsing cost on every press. `null` means the body isn't a
  // voice envelope and the hook should stay inert.
  const source = useMemo<VoiceSource | null>(
    () => resolveSource(message),
    [message, resolveSource],
  );

  // Async waveform extraction. Driver may decode the audio (web's
  // AudioContext path) or return a deterministic fallback (desktop).
  // Either way we replace the seed-derived bars once a real waveform
  // is available.
  useEffect(() => {
    if (driver === null || source === null) return;
    let cancelled = false;
    void driver
      .extractWaveform(source, message.id)
      .then((bars) => {
        if (cancelled || !mountedRef.current) return;
        if (Array.isArray(bars) && bars.length > 0) {
          setWaveform(bars);
        }
      })
      .catch(() => {
        // Best-effort — the seed-derived fallback is already showing.
      });
    return () => {
      cancelled = true;
    };
  }, [driver, source, message.id]);

  // Cleanup on unmount or when the active message changes — stop any
  // in-flight playback so the underlying audio element is released.
  useEffect(() => {
    // Reset on (re)mount. CRITICAL for React StrictMode (dev): the double
    // mount→unmount→remount runs the cleanup below once, setting
    // `mountedRef.current = false`; without re-setting it true here it
    // stays false forever, so `togglePlay`'s resolve handler treats the
    // component as unmounted and instantly calls `handle.stop()` →
    // `audio.pause()` aborts `audio.play()` ("interrupted by pause()") and
    // nothing ever plays. Re-affirming true on mount fixes playback.
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      const h = handleRef.current;
      handleRef.current = null;
      if (h !== null) {
        try {
          h.stop();
        } catch {
          // Best-effort — driver-level cleanup failures are non-fatal.
        }
      }
    };
  }, []);

  const togglePlay = useCallback((): void => {
    if (driver === null || source === null) {
      // Surface WHY playback can't start instead of silently no-oping —
      // the two failure modes are "no <VoicePlaybackProvider> in the
      // tree" (driver null) and "body isn't a decodable voice envelope"
      // (source null). Without this the bubble just does nothing on tap.
      if (typeof console !== 'undefined') {
        console.warn('[voice] playback unavailable', {
          driver: driver !== null,
          source: source !== null,
        });
      }
      onError?.(new Error(driver === null ? 'no-playback-driver' : 'no-voice-source'));
      return;
    }
    if (handleRef.current !== null || startingRef.current) {
      // Already playing (handle resolved) OR still starting up — stop
      // and reset to idle. When still starting, flipping `startingRef`
      // off makes the in-flight `.then` below release the handle the
      // moment it resolves, so the tap reliably pauses either way.
      startingRef.current = false;
      if (handleRef.current !== null) {
        try {
          handleRef.current.stop();
        } catch {
          // Ignore — clearing the local state is what matters.
        }
        handleRef.current = null;
      }
      setIsPlaying(false);
      setProgressMs(0);
      return;
    }
    startingRef.current = true;
    setIsPlaying(true);
    void driver
      .play({
        source,
        onProgress: (ms: number) => {
          if (!mountedRef.current) return;
          setProgressMs(ms);
        },
        onEnded: () => {
          if (!mountedRef.current) return;
          startingRef.current = false;
          handleRef.current = null;
          setIsPlaying(false);
          setProgressMs(0);
        },
        onDuration: (ms: number) => {
          if (!mountedRef.current) return;
          if (ms > 0) setDurationMs(ms);
        },
        rate: rateRef.current,
        ...(onError !== undefined ? { onError } : {}),
      })
      .then((handle) => {
        if (!mountedRef.current || !startingRef.current) {
          // Component unmounted, OR the user tapped again during
          // startup to cancel — release the handle immediately and stay
          // idle.
          try {
            handle.stop();
          } catch {
            // Best-effort.
          }
          return;
        }
        startingRef.current = false;
        handleRef.current = handle;
      })
      .catch((err: unknown) => {
        startingRef.current = false;
        if (!mountedRef.current) return;
        // Failure to spin up the audio element (undecodable codec,
        // autoplay blocked, …). Surface it instead of failing silently,
        // then reset to idle.
        try {
          onError?.(err);
        } catch {
          // Never let an error handler break the reset below.
        }
        handleRef.current = null;
        setIsPlaying(false);
        setProgressMs(0);
      });
  }, [driver, source, onError]);

  const setRate = useCallback((rate: number): void => {
    if (!(rate > 0)) return;
    rateRef.current = rate;
    handleRef.current?.setRate?.(rate);
  }, []);

  return { isPlaying, progressMs, durationMs, togglePlay, setRate, waveform };
}
